import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, Observable, of } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { NavigateType, PageType } from '@blaze-shared/api-contracts-domain';
import { fromRouter } from '@blaze-shared/app-state-data-access';
import { syncSelect } from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeTableSchema,
  DatasetRow,
  DatasetSchema,
  normalizeDataRow,
  WorkflowSchema,
} from '@blaze-shared/schema-domain';

export interface NavigateInitResult {
  isInEditor: boolean;

  /**
   * Normalized data row
   */
  inputDataRow: DatasetRow | null;
  inputDataRowSchema: WorkflowSchema;

  /**
   * The actual page type might be different then the user selected.
   * Make sure the Dialog-like page is opened on an overlay
   * and a page is opened as a page.
   */
  adjustedNavigateType: NavigateType;

  /**
   * The actual page type of the target page.
   */
  pageType: PageType | undefined;

  /**
   * Schema of the main dataset of the page.
   * Based on its `mainDatasetId`.
   */
  mainDatasetSchema: DatasetSchema | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class NavigatePrepareDataService {
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);

  /**
   * Gathers the necessary data to execute the node.
   */
  prepareData(
    navigateType: NavigateType,
    target: string,
    inputData: unknown,
    inputSchema: WorkflowSchema,
  ): Observable<NavigateInitResult> {
    //
    const isInEditor = syncSelect(this.store, fromRouter.getIsInEditor);

    let mainDatasetSchema$: Observable<BlazeTableSchema | DatasetSchema | undefined> =
      of(undefined);
    let adjustedNavigateType: NavigateType = navigateType;
    let pageType: PageType | undefined;

    // For Page and Dialog options - prepare main dataset schema and adjust navigate type if needed
    if (navigateType === 'Page' || navigateType === 'Dialog') {
      const runtimePages = syncSelect(this.store, runtimeSelectors.selectRuntimePages);
      const runtimePage = runtimePages?.[target];

      if (runtimePage?.mainDatasetId) {
        // Fetch appropriate main dataset schema if necessary.
        // `obtainSchema()` for now as `get()` is buggy until future release.
        mainDatasetSchema$ = this.schemaService.obtainSchema(runtimePage.mainDatasetId);
      }

      // Check the actual page type to avoid opening page as a dialog and similar
      pageType = runtimePage?.pageType;
      adjustedNavigateType = getAdjustedNavigateType(adjustedNavigateType, pageType);
    }

    return mainDatasetSchema$.pipe(
      map((mainDatasetSchema) => ({
        isInEditor,
        adjustedNavigateType,
        pageType,
        mainDatasetSchema,
        inputDataRow: normalizeDataRow(inputData),
        inputDataRowSchema: inputSchema,
      })),
    );
  }
}

/**
 * This replaces the navigate type based on the current page type.
 * (The navigation page type might be outdated and mismatching the current page type)
 */
function getAdjustedNavigateType(
  navigateType: NavigateType,
  pageType: PageType | undefined,
): NavigateType {
  if (pageType === 'Dialog' || pageType === 'Drawer') {
    return 'Dialog';
  } else if (pageType === 'Page') {
    return 'Page';
  }

  return navigateType;
}
